<template>
  <div class="profile-page">
    <!-- Header with avatar, nickname, bio -->
    <div class="profile-header">
      <div class="avatar" @click="showEditProfile = true">
        <img v-if="profile.avatarUrl" :src="profile.avatarUrl" alt="" />
        <van-icon v-else name="user-o" size="36" color="#6366f1" />
        <div class="avatar-tip"><van-icon name="photograph" size="14" color="#fff" /></div>
      </div>
      <div class="user-info">
        <div class="username">{{ profile.nickname || user.username }}</div>
        <div class="user-role">
          <van-tag :type="profile.isApproved ? 'success' : 'warning'" size="small">
            {{ profile.isApproved ? '已认证' : '审核中' }}
          </van-tag>
        </div>
        <div class="bio" v-if="profile.bio">{{ profile.bio }}</div>
        <div class="bio bio-empty" v-else>这个人很懒，什么都没写</div>
      </div>
      <van-icon name="setting-o" class="edit-btn" @click="showEditProfile = true" />
    </div>

    <!-- <van-notice-bar
      v-if="!profile.isApproved"
      text="您的账号正在审核中，请耐心等待"
      left-icon="info-o"
      color="#f59e0b"
      background="#fef3c7"
    /> -->

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon" style="background:#eef2ff">
          <van-icon name="balance-o" size="20" color="#6366f1" />
        </div>
        <div class="stat-info">
          <div class="stat-label">累计收入</div>
          <div class="stat-value">¥{{ formatNum(profile.totalIncome) }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background:#fef2f2">
          <van-icon name="gold-coin-o" size="20" color="#ef4444" />
        </div>
        <div class="stat-info">
          <div class="stat-label">可提现</div>
          <div class="stat-value highlight">¥{{ formatNum(profile.withdrawable) }}</div>
        </div>
      </div>
    </div>

    <!-- Menu -->
    <div class="menu-group">
      <van-cell title="分享主页" icon="share-o" is-link @click="onShare">
        <template #label>
          <span class="cell-desc">生成链接，分享给粉丝查看资源</span>
        </template>
      </van-cell>
      <van-cell title="提现管理" icon="balance-o" is-link @click="$router.push('/withdraw')">
        <template #label>
          <span class="cell-desc">申请提现、查看提现记录</span>
        </template>
      </van-cell>
      <van-cell title="收款信息" icon="records-o" is-link
        :label="profile.alipayAccount ? '支付宝: ' + profile.alipayAccount : '完善收款信息'"
        @click="showEditPayment = true"
      />
    </div>

    <div class="menu-group">
      <van-cell title="退出登录" icon="revoke" @click="onLogout" clickable>
        <template #title>
          <span style="color:#ef4444">退出登录</span>
        </template>
      </van-cell>
    </div>

    <!-- Share Popup -->
    <van-popup v-model="showShare" position="bottom" round :style="{ height: 'auto' }">
      <div class="share-popup">
        <h3>分享主页</h3>
        <p class="share-desc">复制下方链接发给粉丝，对方打开即可查看你的资源列表</p>
        <div class="share-link-box">
          <div class="share-link">{{ shareUrl }}</div>
        </div>
        <van-button round block color="linear-gradient(135deg, #6366f1, #8b5cf6)" @click="onCopyLink">
          一键复制链接
        </van-button>
        <div class="share-cancel" @click="showShare = false">取消</div>
      </div>
    </van-popup>

    <!-- Edit Personal Info Popup -->
    <van-popup v-model="showEditProfile" position="bottom" round :style="{ height: '70%' }">
      <div class="edit-popup">
        <h3>编辑个人信息</h3>
        <div class="avatar-upload">
          <van-uploader
            :after-read="onAvatarUpload"
            accept="image/*"
            preview-size="80px"
            :max-count="1"
          >
            <div class="avatar-preview">
              <img v-if="profile.avatarUrl" :src="profile.avatarUrl" alt="" />
              <van-icon v-else name="user-o" size="40" color="#cbd5e1" />
            </div>
          </van-uploader>
          <span class="avatar-upload-tip">点击更换头像</span>
        </div>
        <van-form @submit="onUpdateProfile">
          <van-field
            v-model="editForm.nickname"
            label="昵称"
            placeholder="给自己取个名字吧"
            maxlength="20"
          />
          <van-field
            v-model="editForm.bio"
            label="简介"
            placeholder="介绍一下自己..."
            type="textarea"
            rows="3"
            maxlength="100"
            show-word-limit
          />
          <van-button
            round block native-type="submit"
            :loading="updatingProfile"
            color="linear-gradient(135deg, #6366f1, #8b5cf6)"
            style="margin-top:20px"
          >
            保存
          </van-button>
        </van-form>
      </div>
    </van-popup>

    <!-- Edit Payment Info Popup -->
    <van-popup v-model="showEditPayment" position="bottom" round :style="{ height: '40%' }">
      <div class="edit-popup">
        <h3>收款信息</h3>
        <van-form @submit="onUpdatePayment">
          <van-field
            v-model="editPayment.realName"
            label="真实姓名"
            placeholder="请输入真实姓名"
          />
          <van-field
            v-model="editPayment.alipayAccount"
            label="支付宝账号"
            placeholder="请输入支付宝账号"
          />
          <van-button
            round block native-type="submit"
            :loading="updatingPayment"
            color="linear-gradient(135deg, #6366f1, #8b5cf6)"
            style="margin-top:20px"
          >
            保存
          </van-button>
        </van-form>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { bloggerApi, uploadApi } from '../api';
import { compressImage } from '../utils/imageCompress';

export default {
  data() {
    return {
      profile: {
        isApproved: true, totalIncome: 0, withdrawable: 0,
        alipayAccount: '', realName: '', avatarUrl: '',
        nickname: '', bio: ''
      },
      showShare: false,
      showEditProfile: false, showEditPayment: false,
      editForm: { nickname: '', bio: '' },
      editPayment: { realName: '', alipayAccount: '' },
      updatingProfile: false, updatingPayment: false
    };
  },
  computed: {
    user() { return this.$store.state.user || {}; },
    shareUrl() {
      if (!this.user.id) return '';
      const base = process.env.VUE_APP_SHARE_BASE || 'https://user.wpan.top';
      return base + '/#/blogger/' + this.user.id;
    }
  },
  created() { this.fetchProfile(); },
  methods: {
    async fetchProfile() {
      try {
        const res = await bloggerApi.getProfile();
        const d = res.data;
        this.profile = {
          isApproved: d.isApproved,
          totalIncome: d.totalIncome || 0,
          withdrawable: d.withdrawable || 0,
          alipayAccount: d.alipayAccount || '',
          realName: d.realName || '',
          avatarUrl: d.avatarUrl || '',
          nickname: d.nickname || '',
          bio: d.bio || ''
        };
        this.editForm = { nickname: d.nickname || '', bio: d.bio || '' };
        this.editPayment = { realName: d.realName || '', alipayAccount: d.alipayAccount || '' };
      } catch (e) { /* ignore */ }
    },
    async onAvatarUpload(file) {
      try {
        const compressed = await compressImage(file.file || file);
        const res = await uploadApi.upload(compressed);
        const url = res.data.url;
        this.profile.avatarUrl = url;
        await bloggerApi.updateProfile({ avatarUrl: url });
        this.$toast.success('头像已更新');
      } catch (e) {
        this.$toast.fail('上传失败');
      }
    },
    async onUpdateProfile() {
      this.updatingProfile = true;
      try {
        await bloggerApi.updateProfile(this.editForm);
        this.$toast.success('保存成功');
        this.showEditProfile = false;
        this.fetchProfile();
      } catch (e) { /* handled */ }
      finally { this.updatingProfile = false; }
    },
    async onUpdatePayment() {
      this.updatingPayment = true;
      try {
        await bloggerApi.updateProfile(this.editPayment);
        this.$toast.success('保存成功');
        this.showEditPayment = false;
        this.fetchProfile();
      } catch (e) { /* handled */ }
      finally { this.updatingPayment = false; }
    },
    onShare() { this.showShare = true; },
    async onCopyLink() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.$toast.success('链接已复制');
        this.showShare = false;
      } catch (e) {
        const textarea = document.createElement('textarea');
        textarea.value = this.shareUrl;
        textarea.style.position = 'fixed'; textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          this.$toast.success('链接已复制');
          this.showShare = false;
        } catch (e2) {
          this.$toast.fail('复制失败，请手动复制');
        }
        document.body.removeChild(textarea);
      }
    },
    onLogout() {
      this.$dialog.confirm({
        title: '退出登录',
        message: '确定要退出登录吗？'
      }).then(() => {
        this.$store.dispatch('logout');
        this.$router.replace('/login');
      }).catch(() => {});
    },
    formatNum(n) { return (parseFloat(n) || 0).toFixed(2); }
  }
};
</script>

<style scoped>
.profile-page { min-height: 100vh; background: #f5f7fa; }
.profile-header {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 24px 20px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff; position: relative;
}
.avatar {
  width: 68px; height: 68px; border-radius: 50%;
  background: rgba(255,255,255,0.95);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; position: relative; overflow: visible; cursor: pointer;
}
.avatar img {
  width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
}
.avatar-tip {
  position: absolute; bottom: -2px; right: -2px;
  width: 24px; height: 24px; border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #fff;
}
.user-info { flex: 1; min-width: 0; }
.username { font-size: 20px; font-weight: 700; }
.user-role { margin-top: 4px; }
.bio {
  font-size: 13px; opacity: 0.85; margin-top: 6px;
  line-height: 1.5; display: -webkit-box;
  -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.bio-empty { font-style: italic; opacity: 0.6; }
.edit-btn {
  position: absolute; top: 20px; right: 20px;
  font-size: 20px; opacity: 0.8;
}
.stats-grid {
  display: flex; gap: 12px; margin: -16px 16px 16px;
  position: relative; z-index: 1;
}
.stat-card {
  flex: 1; display: flex; align-items: center; gap: 10px;
  background: #fff; border-radius: 12px; padding: 14px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}
.stat-icon {
  width: 42px; height: 42px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.stat-info { min-width: 0; }
.stat-label { font-size: 12px; color: #9ca3af; }
.stat-value { font-size: 18px; font-weight: 700; color: #1f2937; }
.stat-value.highlight { color: #ef4444; }
.menu-group {
  margin: 12px 16px; background: #fff;
  border-radius: 12px; overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.cell-desc { font-size: 12px; color: #9ca3af; }

.share-popup { padding: 24px 20px 16px; }
.share-popup h3 { text-align: center; margin: 0 0 8px; font-size: 18px; color: #1f2937; }
.share-desc { text-align: center; font-size: 13px; color: #9ca3af; margin: 0 0 16px; line-height: 1.5; }
.share-link-box {
  background: #f5f7fa; border-radius: 10px;
  padding: 14px; margin-bottom: 16px;
  border: 1px dashed #d1d5db;
}
.share-link { font-size: 14px; color: #6366f1; word-break: break-all; line-height: 1.6; text-align: center; }
.share-cancel { text-align: center; font-size: 14px; color: #9ca3af; padding: 16px 0 8px; cursor: pointer; }

.edit-popup { padding: 24px 16px 32px; }
.edit-popup h3 { text-align: center; margin: 0 0 20px; font-size: 18px; color: #1f2937; }
.avatar-upload {
  display: flex; align-items: center; gap: 16px;
  margin-bottom: 20px; padding: 0 16px;
}
.avatar-preview {
  width: 80px; height: 80px; border-radius: 50%;
  background: #f5f7fa; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  border: 2px dashed #d1d5db;
}
.avatar-preview img {
  width: 100%; height: 100%; object-fit: cover;
}
.avatar-upload-tip { font-size: 13px; color: #9ca3af; }
</style>
